<template>
  <div class="dialog" id="add-room-dialog" aria-hidden="true">
    <div class="dialog-overlay" tabindex="-1" data-a11y-dialog-hide></div>
    <div
      role="dialog"
      class="dialog-content"
      aria-labelledby="my-dialog-title"
      aria-describedby="my-dialog-description"
    >
      <button
        data-a11y-dialog-hide
        class="dialog-close"
        aria-label="Close this dialog window"
      >
        <img
          src="@/assets/icons/dialog-close.svg"
          width="24"
          height="24"
          alt="Dialog close"
        />
      </button>

      <div class="project-add">
        <p class="project-add__heading">
          {{ $t("project_settings.add_room_modal.add_room") }}
        </p>
        <form>
          <div>
            <select-search
              class="margin-0"
              :data="rooms"
              :errors="errors.type"
              :with-initials="false"
              v-model:selected-item="type"
              :set-custom-label="(item) => item.name"
              :label="$t('project_settings.add_room_modal.type')"
              :placeholder="
                $t('project_settings.add_room_modal.select_room_placeholder')
              "
              :empty-result="$t('project_settings.add_room_modal.no_rooms')"
            ></select-search>
          </div>
          <div class="control control--input control-number">
            <label for="count" class="control__label">{{
              $t("project_settings.add_room_modal.amount")
            }}</label>
            <div>
              <button
                type="button"
                data-type="dec"
                @click.prevent="decrementAmount"
              >
                -
              </button>
              <input
                type="text"
                id="count"
                v-model="amount"
                :class="{ error: errors.amount }"
              />
              <button
                type="button"
                data-type="inc"
                @click.prevent="incrementAmount"
              >
                +
              </button>
            </div>
            <div v-show="errors.amount" class="control__error">
              <div class="control__error-inner">
                {{ errors.amount }}
              </div>
            </div>
          </div>
          <div class="project-add__button">
            <button
              @click.prevent="addRoom"
              :class="{ disabled: sendingForm }"
              :disabled="sendingForm"
            >
              <template v-if="!sendingForm">
                {{ $t("project_settings.add_room_modal.add") }}
              </template>
              <template v-else>
                {{ $t("project_settings.add_room_modal.adding") }}
              </template>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref, reactive, watch, inject } from "vue";
import axios from "axios";
import ApiHelper from "@/helpsers/api.helper";
import authHelper from "@/helpsers/auth.helper";
import AlertHelper from "@/helpsers/alert.helper";
import router from "@/router";
import { useI18n } from "vue-i18n";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useProjectSettingsStore } from "@/stores/user/architect/projectSettings.store";
import { storeToRefs } from "pinia";
import SelectSearch from "@/components/fields/select/selectSearch";

export default {
  props: ["type"],
  name: "AddRoomModal",
  components: { SelectSearch },
  emits: ["addNewRoom"],
  setup(props, { emit }) {
    const sendingForm = ref(false);
    let roomToDelete = ref("");
    const addRoomModal = ref();
    let alert = inject("alert");
    const { t } = useI18n();
    const projectSettingsStore = useProjectSettingsStore();
    let { rooms } = storeToRefs(projectSettingsStore);

    onMounted(() => {
      addRoomModal.value = new A11yDialog(
        document.querySelector("#add-room-dialog")
      );
    });

    function showModal() {
      addRoomModal.value.show();
    }

    const { handleSubmit, errors } = useForm({
      validationSchema: yup.object().shape({
        type: yup
          .object()
          .typeError(t("validate.required"))
          .required(t("validate.required")),
        amount: yup
          .number()
          .required(t("validate.required"))
          .min(1, t("validate.min_number", { number: 0 })),
      }),
      initialValues: {
        type: "",
        amount: 1,
      },
    });

    const { value: type } = useField("type");
    const { value: amount } = useField("amount");
    const addRoom = handleSubmit(async (values) => {
      sendingForm.value = true;

      axios
        .post(
          ApiHelper.addRoomsToProjectUrl(router.currentRoute.value.params.id),
          {
            amount: values.amount,
            roomId: values.type.id,
          },
          {
            headers: authHelper.authHeader(),
          }
        )
        .then((response) => {
          sendingForm.value = false;
          emit("addNewRooms", response.data);
          addRoomModal.value.hide();
          AlertHelper.show(
            alert,
            t("project_settings.add_room_modal.alert_success")
          );
        })
        .catch((error) => {
          let errorMessage = ApiHelper.getResponseMessage(
            error?.response?.data?.code
          );
          sendingForm.value = false;
          AlertHelper.show(alert, errorMessage, "", "error");
        });
    });

    function decrementAmount() {
      if (amount.value > 1) amount.value -= 1;
    }

    function incrementAmount() {
      amount.value += 1;
    }

    return {
      roomToDelete,
      showModal,
      sendingForm,
      type,
      amount,
      errors,
      incrementAmount,
      decrementAmount,
      addRoom,
      rooms,
    };
  },
};
</script>

<style scoped lang="scss">
@use "@/css/variables";

.control-number {
  input {
    text-align: center;
  }
}
</style>
